import {
  createDurableLiveUpdatesController,
  type DurableLiveUpdateStatus,
} from "@/lib/data/live-updates/controller";

import {
  matterPhaseStreamFrameSchema,
  type MatterPhaseChangeFrame,
} from "./contract";

export const MATTER_PHASE_STREAM_PROTOCOL = "lawworks-api.matter-phases.v1";

type MatterPhaseStreamSocket = Pick<
  WebSocket,
  "close" | "onclose" | "onerror" | "onmessage" | "onopen" | "protocol"
>;

type MatterPhaseLiveUpdatesDependencies = {
  createSocket?: (url: string, protocols: string[]) => MatterPhaseStreamSocket;
  getAccessToken: (forceRefresh?: boolean) => Promise<string>;
  initialCursor: string;
  matterId: string;
  onChanges: (
    signal: AbortSignal,
    changes?: MatterPhaseChangeFrame[],
  ) => Promise<void>;
  onStatusChange?: (status: DurableLiveUpdateStatus) => void;
  refreshSnapshot: (signal: AbortSignal) => Promise<{ cursor: string }>;
  websocketBaseUrl: string;
};

const buildMatterPhaseStreamUrl = ({
  after,
  matterId,
  websocketBaseUrl,
}: {
  after: string;
  matterId: string;
  websocketBaseUrl: string;
}) => {
  const url = new URL(
    `/matters/${encodeURIComponent(matterId)}/phases/stream`,
    websocketBaseUrl,
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("after", after);
  return url.toString();
};

export const createMatterPhaseLiveUpdatesController = (
  dependencies: MatterPhaseLiveUpdatesDependencies,
) =>
  createDurableLiveUpdatesController<MatterPhaseChangeFrame>({
    buildUrl: (after) =>
      buildMatterPhaseStreamUrl({
        after,
        matterId: dependencies.matterId,
        websocketBaseUrl: dependencies.websocketBaseUrl,
      }),
    createSocket: dependencies.createSocket,
    getAccessToken: dependencies.getAccessToken,
    initialCursor: dependencies.initialCursor,
    onChanges: (changes, signal) => dependencies.onChanges(signal, changes),
    onStatusChange: dependencies.onStatusChange,
    parseFrame: (value) => {
      const parsed = matterPhaseStreamFrameSchema.safeParse(value);
      if (!parsed.success) return undefined;
      if (
        parsed.data.type === "matter.phases_changed" &&
        parsed.data.matterId !== dependencies.matterId
      ) {
        return undefined;
      }
      return parsed.data;
    },
    protocol: MATTER_PHASE_STREAM_PROTOCOL,
    refreshSnapshot: dependencies.refreshSnapshot,
  });
